/**
 * ============================================================
 * 账单模板配置页面 (ReportTemplate) - v65
 * 功能说明：
 *   - 完整中文字段标签（46个）
 *   - 列配置中只显示中文标签
 *   - 列配置可自由拖动排序
 *   - 预览为独立弹窗，选择经纪人+月份查看所有主播数据
 *   - 弹窗必须点击按钮才能关闭
 *   - 移除类型和状态
 * ============================================================
 */

import { useEffect, useState, useCallback, useMemo } from 'react';
import { reportApi, formulaApi } from '../api';
import type { ReportTemplate } from '../types';
import {
  FileText, Plus, Pencil, Trash2, X, RefreshCw, Database,
  ChevronRight, GripVertical, Eye, Sparkles, UserCheck, ChevronDown
} from 'lucide-react';

interface TemplateColumn { fieldKey: string; displayName: string; width: number; visible: boolean; }

// ===== 完整中文字段标签 → 数据库字段映射 =====
const FIELD_LABELS: Record<string, string> = {
  '结算月份': 'settlement_month', '主播昵称': 'anchor_name', '主播uid': 'anchor_uid',
  '主播房间号': 'anchor_room_id', '入会时间': 'join_date', '首次开播时间': 'first_stream_date',
  '经纪人昵称': 'broker_name', '经纪人uid': 'broker_uid',
  '招募经纪人昵称': 'recruit_broker_name', '招募经纪人uid': 'recruit_broker_uid',
  '主播支付': 'anchor_payment', '主播总激励': 'anchor_total_incentive',
  '主播非自提礼物收益': 'anchor_non_self_gift_income', '非自提合计底薪': 'non_self_base_salary',
  '合计活动收益': 'total_activity_income', '非自提运营奖励': 'non_self_operation_reward',
  '非自提运营惩罚': 'non_self_operation_penalty', '主播负向治理收益总扣除': 'governance_deduction',
  '主播负向治理收益总补回': 'governance_recovery', '开播时长(含连麦时长)': 'stream_duration',
  '有效开播天': 'valid_stream_days', '有效开播时长': 'valid_stream_hours',
  '是否入会无首播主播': 'is_no_first_stream', '是否TOP主播': 'is_top_anchor',
  'TOPstar主播评级': 'topstar_level', '总流水': 'total_stream',
  '是否是政策新主播': 'is_policy_new_anchor', '新主播任务流水': 'new_anchor_task_stream',
  '新主播激励分成': 'new_anchor_incentive_share', '新主播激励': 'new_anchor_incentive',
  '新主播结算周期': 'new_anchor_settlement_period', '直播有效天/新主播周期': 'valid_days_in_period',
  '直播有效时长/新主播周期': 'valid_hours_in_period', '当月新主播流水': 'current_month_new_anchor_stream',
  '次月待结算新主播流水': 'next_month_pending_new_anchor_stream', '老主播任务流水': 'old_anchor_task_stream',
  '老主播任务分成': 'old_anchor_task_share', '老主播任务激励': 'old_anchor_task_incentive',
  'TOP主播任务分成': 'top_anchor_task_share', 'TOP主播任务激励': 'top_anchor_task_incentive',
  '成长任务分成': 'growth_task_share', '成长任务激励': 'growth_task_incentive',
  '流水任务分成': 'stream_task_share', '流水任务激励': 'stream_task_incentive',
  '优质奖励': 'quality_reward', '未成年扣除': 'minor_deduction',
};

const DB_TO_LABEL: Record<string, string> = {};
for (const [label, dbField] of Object.entries(FIELD_LABELS)) DB_TO_LABEL[dbField] = label;

const STRING_FIELDS = new Set([
  'settlement_month', 'anchor_name', 'anchor_uid', 'anchor_room_id',
  'join_date', 'first_stream_date', 'broker_name', 'broker_uid',
  'recruit_broker_name', 'recruit_broker_uid',
  'is_no_first_stream', 'is_top_anchor', 'topstar_level',
  'is_policy_new_anchor', 'new_anchor_settlement_period',
]);

const FIELD_GROUPS = [
  { name: '时间类', fields: ['结算月份', '入会时间', '首次开播时间', '新主播结算周期'] },
  { name: '支付类', fields: ['主播支付', '主播总激励', '非自提合计底薪', '合计活动收益', '非自提运营奖励', '非自提运营惩罚', '主播负向治理收益总扣除', '主播负向治理收益总补回'] },
  { name: '流水类', fields: ['总流水', '主播非自提礼物收益', '新主播任务流水', '当月新主播流水', '次月待结算新主播流水', '老主播任务流水'] },
  { name: '分成类', fields: ['新主播激励分成', '新主播激励', '老主播任务分成', '老主播任务激励', 'TOP主播任务分成', 'TOP主播任务激励', '成长任务分成', '成长任务激励', '流水任务分成', '流水任务激励', '优质奖励', '未成年扣除'] },
  { name: '周期类', fields: ['开播时长(含连麦时长)', '有效开播天', '有效开播时长', '直播有效天/新主播周期', '直播有效时长/新主播周期'] },
  { name: '信息类', fields: ['主播昵称', '主播uid', '主播房间号', '是否入会无首播主播', '是否TOP主播', 'TOPstar主播评级', '是否是政策新主播'] },
  { name: '身份类', fields: ['经纪人昵称', '经纪人uid', '招募经纪人昵称', '招募经纪人uid'] },
];

export default function ReportTemplatePage() {
  // ===== 列表状态 =====
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [brokers, setBrokers] = useState<{ name: string }[]>([]);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // ===== 编辑弹窗状态 =====
  const [showEdit, setShowEdit] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<ReportTemplate | null>(null);
  const [editForm, setEditForm] = useState({ name: '', columns: [] as TemplateColumn[] });
  const [showFieldPanel, setShowFieldPanel] = useState(true);
  const [fieldFilter, setFieldFilter] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(FIELD_GROUPS.map(g => g.name)));
  const [saving, setSaving] = useState(false);
  const [dragColIdx, setDragColIdx] = useState<number | null>(null);

  // ===== 预览弹窗状态 =====
  const [showPreview, setShowPreview] = useState(false);
  const [previewBroker, setPreviewBroker] = useState('');
  const [previewMonths, setPreviewMonths] = useState<string[]>([]);
  const [previewMonth, setPreviewMonth] = useState('');
  const [previewAnchors, setPreviewAnchors] = useState<Record<string, unknown>[]>([]);
  const [previewColumns, setPreviewColumns] = useState<TemplateColumn[]>([]);

  // ===== 生成弹窗状态 =====
  const [showGenerate, setShowGenerate] = useState(false);
  const [generateForm, setGenerateForm] = useState({ templateId: 0, month: '' });

  const showSuccess = useCallback((m: string) => { setToast({ message: m, type: 'success' }); setTimeout(() => setToast(null), 3000); }, []);
  const showError = useCallback((m: string) => { setToast({ message: m, type: 'error' }); setTimeout(() => setToast(null), 3000); }, []);

  // ===== 加载数据 =====
  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [t, brokerList] = await Promise.all([reportApi.templates(), formulaApi.brokerList()]);
      setTemplates(t); setBrokers(brokerList);
    } catch { showError('加载失败'); }
    finally { setLoading(false); }
  };

  // ===== 选择经纪人后加载月份 =====
  useEffect(() => {
    if (previewBroker) {
      formulaApi.brokerMonths(previewBroker).then(months => {
        setPreviewMonths(months);
        if (months.length > 0 && !previewMonth) setPreviewMonth(months[0]);
      }).catch(() => setPreviewMonths([]));
    } else {
      setPreviewMonths([]); setPreviewMonth(''); setPreviewAnchors([]);
    }
  }, [previewBroker]);

  // ===== 打开预览弹窗 =====
  const openPreview = (columns: TemplateColumn[]) => {
    setPreviewColumns(columns);
    setPreviewBroker('');
    setPreviewMonth('');
    setPreviewAnchors([]);
    setPreviewMonths([]);
    setShowPreview(true);
  };

  // ===== 预览数据 =====
  const handlePreview = async () => {
    if (!previewBroker) { showError('请选择经纪人'); return; }
    if (!previewMonth) { showError('请选择月份'); return; }
    try {
      const data = await formulaApi.brokerMonthAnchors(previewBroker, previewMonth);
      setPreviewAnchors(data);
    } catch { showError('预览数据加载失败'); }
  };

  const getPreviewValue = (row: Record<string, unknown>, fieldKey: string): string => {
    if (!row) return '-';
    const val = row[fieldKey];
    if (val === undefined || val === null || val === '') return '-';
    if (!STRING_FIELDS.has(fieldKey)) { const n = Number(val); return isNaN(n) ? String(val) : n.toLocaleString(); }
    return String(val);
  };

  // ===== 字段选择器 =====
  const groupedAvailable = useMemo(() => {
    const selected = new Set(editForm.columns.map(c => c.fieldKey));
    const g: Record<string, { label: string; key: string }[]> = {};
    FIELD_GROUPS.forEach(group => {
      const fields = group.fields
        .filter(label => !selected.has(FIELD_LABELS[label]))
        .filter(label => !fieldFilter.trim() || label.toLowerCase().includes(fieldFilter.toLowerCase()))
        .map(label => ({ label, key: FIELD_LABELS[label] }));
      if (fields.length > 0) g[group.name] = fields;
    });
    return g;
  }, [editForm.columns, fieldFilter]);

  // ===== 列操作 =====
  const addColumn = (fieldKey: string) => {
    if (editForm.columns.some(c => c.fieldKey === fieldKey)) return;
    const label = DB_TO_LABEL[fieldKey] || fieldKey;
    setEditForm(prev => ({ ...prev, columns: [...prev.columns, { fieldKey, displayName: label, width: 120, visible: true }] }));
  };
  const removeColumn = (index: number) => setEditForm(prev => ({ ...prev, columns: prev.columns.filter((_, i) => i !== index) }));
  const updateColumn = (index: number, updates: Partial<TemplateColumn>) => setEditForm(prev => ({ ...prev, columns: prev.columns.map((c, i) => i === index ? { ...c, ...updates } : c) }));

  // ===== 拖拽排序 =====
  const handleColDragStart = (index: number, e: React.DragEvent) => { setDragColIdx(index); e.dataTransfer.effectAllowed = 'move'; };
  const handleColDragOver = (targetIdx: number, e: React.DragEvent) => {
    e.preventDefault();
    if (dragColIdx !== null && dragColIdx !== targetIdx) {
      setEditForm(prev => { const cols = [...prev.columns]; const [removed] = cols.splice(dragColIdx, 1); cols.splice(targetIdx, 0, removed); return { ...prev, columns: cols }; });
      setDragColIdx(targetIdx);
    }
  };
  const handleColDragEnd = () => setDragColIdx(null);
  const toggleGroup = (name: string) => { const next = new Set(expandedGroups); if (next.has(name)) next.delete(name); else next.add(name); setExpandedGroups(next); };

  // ===== 编辑弹窗 =====
  const openEdit = (t: ReportTemplate | null) => {
    setEditingTemplate(t);
    if (t) { const cfg = t.config || {}; setEditForm({ name: t.name, columns: cfg.columns || [] }); }
    else setEditForm({ name: '', columns: [] });
    setShowEdit(true);
  };

  const handleSave = async () => {
    if (!editForm.name.trim()) { showError('请输入模板名称'); return; }
    setSaving(true);
    try {
      const payload = { name: editForm.name, type: 'settlement', status: 'active', config: { columns: editForm.columns } };
      if (editingTemplate) { await reportApi.update(editingTemplate.id, payload); showSuccess('更新成功'); }
      else { await reportApi.create(payload); showSuccess('创建成功'); }
      setShowEdit(false); await loadData();
    } catch (err: any) { showError(err.message || '操作失败'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: number) => { if (!confirm('确定删除?')) return; try { await reportApi.delete(id); showSuccess('删除成功'); await loadData(); } catch { showError('删除失败'); } };
  const openGenerate = (t: ReportTemplate) => { setGenerateForm({ templateId: t.id, month: new Date().toISOString().slice(0, 7) }); setShowGenerate(true); };
  const handleGenerate = async () => { if (!generateForm.month) { showError('请选择月份'); return; } setSaving(true); try { await reportApi.generate(generateForm); showSuccess('账单生成成功'); setShowGenerate(false); } catch (err: any) { showError(err.message || '生成失败'); } finally { setSaving(false); } };

  // ===== 可见列 =====
  const visibleColumns = editForm.columns.filter(c => c.visible);

  return (
    <div className="space-y-6">
      {/* 标题 */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2"><FileText className="w-6 h-6 text-[#F59E0B]" />账单模板配置</h1>
        <div className="flex gap-2">
          <button onClick={loadData} className="flex items-center gap-2 px-4 py-2 bg-[#334155] hover:bg-[#475569] rounded-lg text-sm transition-all"><RefreshCw className="w-4 h-4" />刷新</button>
          <button onClick={() => openEdit(null)} className="flex items-center gap-2 px-4 py-2 bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-lg text-sm transition-all"><Plus className="w-4 h-4" />新建模板</button>
        </div>
      </div>

      {/* 模板列表 */}
      {loading ? <div className="flex items-center justify-center py-16"><div className="w-8 h-8 border-2 border-[#3B82F6] border-t-transparent rounded-full animate-spin" /></div>
      : templates.length === 0 ? <div className="text-center py-16 text-[#94A3B8]">暂无模板</div>
      : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates.map(t => {
          const columns = (t.config?.columns || []) as TemplateColumn[];
          return (
            <div key={t.id} className="bg-[#1E293B] border border-[#334155] rounded-xl p-5 hover:border-[#3B82F6]/30 transition-all">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#3B82F6]/10 flex items-center justify-center"><FileText className="w-5 h-5 text-[#3B82F6]" /></div>
                  <div><h3 className="font-medium">{t.name}</h3><p className="text-xs text-[#94A3B8]">{columns.length}列配置</p></div>
                </div>
              </div>
              {columns.length > 0 && <div className="mb-3 flex flex-wrap gap-1">
                {columns.filter(c => c.visible).slice(0, 8).map(c => (
                  <span key={c.fieldKey} className="px-1.5 py-0.5 bg-[#334155]/50 rounded text-[10px] text-[#94A3B8]">{c.displayName}</span>
                ))}
                {columns.filter(c => c.visible).length > 8 && <span className="text-[10px] text-[#64748B]">+{columns.filter(c => c.visible).length - 8}</span>}
              </div>}
              <div className="flex gap-2 pt-3 border-t border-[#334155]">
                <button onClick={() => openGenerate(t)} className="flex-1 py-2 bg-[#F59E0B]/10 hover:bg-[#F59E0B]/20 text-[#F59E0B] rounded text-xs flex items-center justify-center gap-1 transition-colors"><Sparkles className="w-3.5 h-3.5" />生成账单</button>
                <button onClick={() => openPreview(columns)} className="p-2 text-[#94A3B8] hover:text-[#3B82F6] hover:bg-[#3B82F6]/10 rounded transition-colors" title="预览"><Eye className="w-4 h-4" /></button>
                <button onClick={() => openEdit(t)} className="p-2 text-[#94A3B8] hover:text-[#3B82F6] hover:bg-[#3B82F6]/10 rounded transition-colors"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => handleDelete(t.id)} className="p-2 text-[#94A3B8] hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          );
        })}
      </div>}

      {/* ===== 编辑弹窗（单列布局，无预览） ===== */}
      {showEdit && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-[#1E293B] border border-[#334155] rounded-xl p-6 w-full max-w-3xl max-h-[90vh] overflow-auto">
            {/* 头部 */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">{editingTemplate ? '编辑模板' : '新建模板'}</h3>
              <button onClick={() => setShowEdit(false)} className="text-[#64748B] hover:text-white p-1 hover:bg-[#334155] rounded transition-all"><X className="w-5 h-5" /></button>
            </div>

            {/* 模板名称 */}
            <div className="mb-4">
              <label className="block text-xs text-[#94A3B8] mb-1.5">模板名称 *</label>
              <input value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} className="w-full bg-[#0F172A] border border-[#334155] rounded-lg px-4 py-2.5 text-sm focus:border-[#3B82F6] focus:outline-none text-[#F1F5F9]" placeholder="模板名称" />
            </div>

            {/* 列配置 */}
            <div className="border border-[#334155] rounded-lg overflow-hidden">
              <div className="px-4 py-3 bg-[#0F172A] flex items-center justify-between cursor-pointer" onClick={() => setShowFieldPanel(!showFieldPanel)}>
                <div className="flex items-center gap-2"><Database className="w-4 h-4 text-[#8B5CF6]" /><span className="text-sm font-medium">列配置</span><span className="px-2 py-0.5 bg-[#8B5CF6]/10 text-[#8B5CF6] rounded-full text-xs">{editForm.columns.length}列</span></div>
                <ChevronRight className={`w-4 h-4 text-[#94A3B8] transition-transform ${showFieldPanel ? 'rotate-90' : ''}`} />
              </div>
              {showFieldPanel && (
                <div className="p-4 space-y-4">
                  {/* 已选列 */}
                  {editForm.columns.length > 0 && (
                    <div>
                      <h4 className="text-xs text-[#94A3B8] mb-2 flex items-center gap-1"><GripVertical className="w-3 h-3" />已选列（拖动排序）</h4>
                      <div className="space-y-1 max-h-[300px] overflow-auto">
                        {editForm.columns.map((col, index) => (
                          <div
                            key={col.fieldKey}
                            draggable
                            onDragStart={(e) => handleColDragStart(index, e)}
                            onDragOver={(e) => handleColDragOver(index, e)}
                            onDragEnd={handleColDragEnd}
                            className={`flex items-center gap-2 bg-[#0F172A] border border-[#334155] rounded-lg px-3 py-2 cursor-grab active:cursor-grabbing transition-all ${dragColIdx === index ? 'opacity-50 border-[#3B82F6]' : ''}`}
                          >
                            <GripVertical className="w-3.5 h-3.5 text-[#475569] shrink-0" />
                            <span className="flex-1 text-xs text-[#F1F5F9] truncate">{col.displayName}</span>
                            <div className="flex items-center gap-1"><span className="text-[10px] text-[#64748B]">宽</span><input type="number" value={col.width} onChange={e => updateColumn(index, { width: Number(e.target.value) || 100 })} className="w-14 h-7 px-2 bg-[#1E293B] border border-[#334155] rounded text-xs text-[#F1F5F9] focus:border-[#3B82F6] focus:outline-none" /></div>
                            <button onClick={() => updateColumn(index, { visible: !col.visible })} className={`p-1 rounded ${col.visible ? 'text-emerald-400' : 'text-[#64748B]'}`} title={col.visible ? '可见' : '隐藏'}>{col.visible ? <Eye className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5 opacity-30" />}</button>
                            <button onClick={() => removeColumn(index)} className="p-1 text-red-400 hover:bg-red-500/10 rounded"><X className="w-3.5 h-3.5" /></button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* 添加字段 */}
                  <div>
                    <h4 className="text-xs text-[#94A3B8] mb-2">添加字段（{Object.keys(FIELD_LABELS).length}个）</h4>
                    <input type="text" value={fieldFilter} onChange={e => setFieldFilter(e.target.value)} placeholder="搜索字段标签..." className="w-full h-8 px-3 mb-2 bg-[#0F172A] border border-[#334155] rounded text-xs text-[#F1F5F9] placeholder-[#475569] focus:outline-none focus:border-[#3B82F6]" />
                    <div className="space-y-2 max-h-[280px] overflow-auto">
                      {Object.entries(groupedAvailable).map(([cat, fields]) => (
                        <div key={cat}>
                          <button onClick={() => toggleGroup(cat)} className="flex items-center gap-1 text-[10px] text-[#94A3B8] font-medium mb-1 hover:text-white transition-all">
                            {expandedGroups.has(cat) ? <ChevronDown className="w-2.5 h-2.5" /> : <ChevronRight className="w-2.5 h-2.5" />}
                            {cat} ({fields.length})
                          </button>
                          {expandedGroups.has(cat) && (
                            <div className="flex flex-wrap gap-1">
                              {fields.map(f => (
                                <button key={f.key} onClick={() => addColumn(f.key)} className="inline-flex items-center gap-1 px-2 py-1 bg-[#0F172A] hover:bg-[#3B82F6]/20 border border-[#334155] hover:border-[#3B82F6] rounded text-[10px] text-[#94A3B8] hover:text-[#3B82F6] transition-all">
                                  <Plus className="w-2.5 h-2.5" />{f.label}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                      {Object.keys(groupedAvailable).length === 0 && (
                        <p className="text-xs text-[#64748B] text-center py-2">{fieldFilter ? '无匹配字段' : '所有字段已添加'}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* 底部操作栏 */}
            <div className="flex gap-3 mt-4 pt-4 border-t border-[#334155]">
              <button onClick={() => setShowEdit(false)} className="flex-1 py-2.5 border border-[#334155] rounded-lg text-sm hover:bg-[#334555]/30">取消</button>
              {visibleColumns.length > 0 && (
                <button onClick={() => openPreview(editForm.columns)} className="flex-1 py-2.5 bg-[#334155] hover:bg-[#475569] text-white rounded-lg text-sm flex items-center justify-center gap-1"><Eye className="w-4 h-4" />预览</button>
              )}
              <button onClick={handleSave} disabled={saving} className="flex-1 py-2.5 bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-lg text-sm disabled:opacity-50">{saving ? '保存中...' : '保存'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== 预览弹窗（独立，必须点击关闭） ===== */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-[#1E293B] border border-[#334155] rounded-xl p-6 w-full max-w-6xl flex flex-col" style={{ height: '85vh' }}>
            {/* 头部 */}
            <div className="flex items-center justify-between mb-4 shrink-0">
              <h3 className="text-lg font-bold flex items-center gap-2"><Eye className="w-5 h-5 text-[#3B82F6]" />模板预览</h3>
              <button onClick={() => setShowPreview(false)} className="text-[#64748B] hover:text-white p-1 hover:bg-[#334155] rounded transition-all"><X className="w-5 h-5" /></button>
            </div>

            {/* 选择栏 */}
            <div className="flex gap-3 mb-4 shrink-0">
              <div className="flex-1">
                <label className="text-xs text-[#94A3B8] mb-1 block flex items-center gap-1"><UserCheck className="w-3 h-3" />选择经纪人</label>
                <select value={previewBroker} onChange={e => setPreviewBroker(e.target.value)} className="w-full bg-[#0F172A] border border-[#334155] rounded-lg px-3 py-2 text-sm focus:border-[#3B82F6] focus:outline-none text-[#F1F5F9]">
                  <option value="">请选择经纪人</option>
                  {brokers.map(b => (<option key={b.name} value={b.name}>{b.name}</option>))}
                </select>
              </div>
              {previewBroker && previewMonths.length > 0 && (
                <div className="flex-1">
                  <label className="text-xs text-[#94A3B8] mb-1 block">选择月份</label>
                  <select value={previewMonth} onChange={e => setPreviewMonth(e.target.value)} className="w-full bg-[#0F172A] border border-[#334155] rounded-lg px-3 py-2 text-sm focus:border-[#3B82F6] focus:outline-none text-[#F1F5F9]">
                    {previewMonths.map(m => (<option key={m} value={m}>{m}</option>))}
                  </select>
                </div>
              )}
              <div className="flex items-end">
                <button onClick={handlePreview} disabled={!previewBroker} className="px-6 py-2 bg-[#3B82F6] hover:bg-[#2563EB] disabled:opacity-50 disabled:hover:bg-[#3B82F6] text-white rounded-lg text-sm transition-colors">
                  预览
                </button>
              </div>
            </div>

            {/* 数据表格 — 固定高度，内部滚动 */}
            <div className="flex-1 min-h-0 border border-[#334155] rounded-lg">
              {previewColumns.filter(c => c.visible).length > 0 ? (
                <div className="overflow-auto w-full h-full">
                  <table className="w-max min-w-full text-xs border-collapse">
                    <thead className="sticky top-0 z-10">
                      <tr className="bg-[#0F172A]">
                        {previewColumns.filter(c => c.visible).map(col => (
                          <th key={col.fieldKey} className="px-3 py-2 text-left text-[#94A3B8] font-medium border-b border-[#334155] whitespace-nowrap" style={{ minWidth: col.width }}>
                            {col.displayName}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewAnchors.length > 0 ? previewAnchors.map((row, rowIdx) => (
                        <tr key={rowIdx} className="hover:bg-[#1E293B]/50 border-b border-[#334155]/30">
                          {previewColumns.filter(c => c.visible).map(col => (
                            <td key={col.fieldKey} className="px-3 py-2 text-[#F1F5F9] whitespace-nowrap">
                              {getPreviewValue(row, col.fieldKey)}
                            </td>
                          ))}
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan={previewColumns.filter(c => c.visible).length} className="px-3 py-8 text-[#64748B] text-center">
                            选择经纪人和月份，点击预览加载数据
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-[#64748B] text-sm">模板中没有可见字段</div>
              )}
            </div>

            {/* 底部 */}
            {previewAnchors.length > 0 && (
              <div className="mt-2 text-[10px] text-[#64748B] shrink-0">共 {previewAnchors.length} 条主播记录</div>
            )}
            <div className="flex justify-end mt-4 pt-4 border-t border-[#334155] shrink-0">
              <button onClick={() => setShowPreview(false)} className="px-6 py-2.5 border border-[#334155] rounded-lg text-sm hover:bg-[#334555]/30">关闭</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== 生成账单弹窗 ===== */}
      {showGenerate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-[#1E293B] border border-[#334155] rounded-xl p-6 w-full max-w-md space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold flex items-center gap-2"><FileText className="w-5 h-5 text-[#F59E0B]" />生成账单</h3>
              <button onClick={() => setShowGenerate(false)} className="text-[#64748B] hover:text-white p-1 hover:bg-[#334155] rounded transition-all"><X className="w-5 h-5" /></button>
            </div>
            <div>
              <label className="block text-xs text-[#94A3B8] mb-1.5">结算月份</label>
              <input type="month" value={generateForm.month} onChange={e => setGenerateForm({...generateForm, month: e.target.value})} className="w-full bg-[#0F172A] border border-[#334155] rounded-lg px-4 py-2.5 text-sm focus:border-[#3B82F6] focus:outline-none text-[#F1F5F9]" />
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowGenerate(false)} className="flex-1 py-2.5 border border-[#334155] rounded-lg text-sm hover:bg-[#334555]/30">取消</button>
              <button onClick={handleGenerate} disabled={saving} className="flex-1 py-2.5 bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-lg text-sm disabled:opacity-50">{saving ? '生成中...' : '生成'}</button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className={`fixed bottom-6 right-6 px-4 py-3 rounded-lg text-sm font-medium shadow-lg z-50 ${toast.type === 'success' ? 'bg-emerald-500' : 'bg-red-500'} text-white`}>{toast.message}</div>}
    </div>
  );
}
